import { useEffect, useState, type ReactNode } from 'react'
import { useLivePreview } from './useLivePreview'

export interface MapEditPanelProps {
  title: string
  lat: number
  lng: number
  onSave: (lat: number, lng: number) => void
  onDelete?: () => void
  onCancel: () => void
  onPreview?: (lat: number, lng: number) => void
  // Fed by a drag or a tap-to-place on the map -- overwrites the text
  // fields. Plain values, not a callback, so a normal effect dependency
  // is fine here; the render-loop bug useLivePreview guards against is
  // specifically about depending on a *callback's identity*.
  externalCoord?: { lat: number; lng: number }
  tapPlaceActive?: boolean
  onToggleTapPlace?: () => void
  saving?: boolean
  extraFields?: ReactNode
}

const FIELD = 'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden'

function parseCoord(v: string, max: number): number | null {
  const n = parseFloat(v)
  if (Number.isNaN(n) || Math.abs(n) > max) return null
  return n
}

// Content-only, mirrors mobile's PointEditSheet.tsx -- no positioning of
// its own. The caller (DockedMapEditor) docks this over the map and
// remounts it with a fresh `key` when switching which point is open, so
// local text state resets cleanly on selection change without needing a
// sync effect for that case.
export function MapEditPanel({
  title, lat, lng, onSave, onDelete, onCancel, onPreview, externalCoord,
  tapPlaceActive, onToggleTapPlace, saving = false, extraFields,
}: MapEditPanelProps) {
  const [latText, setLatText] = useState(lat.toFixed(6))
  const [lngText, setLngText] = useState(lng.toFixed(6))
  const [error, setError] = useState('')

  useEffect(() => {
    if (!externalCoord) return
    setLatText(externalCoord.lat.toFixed(6))
    setLngText(externalCoord.lng.toFixed(6))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCoord?.lat, externalCoord?.lng])

  useLivePreview(latText, lngText, onPreview)

  const handleSave = () => {
    const parsedLat = parseCoord(latText, 90)
    const parsedLng = parseCoord(lngText, 180)
    if (parsedLat === null || parsedLng === null) {
      setError('Unesite validne koordinate (lat -90..90, lng -180..180).')
      return
    }
    setError('')
    onSave(parsedLat, parsedLng)
  }

  return (
    <div className="bg-white rounded-overlay shadow-overlay border border-gray-200 p-4 w-96 max-w-[calc(100vw-2rem)] space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-gray-900 truncate">{title}</h4>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-lg leading-none shrink-0" aria-label="Zatvori">×</button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Lat</label>
          <input value={latText} onChange={(e) => setLatText(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-1">Lng</label>
          <input value={lngText} onChange={(e) => setLngText(e.target.value)} className={FIELD} />
        </div>
      </div>

      {extraFields}

      {onToggleTapPlace && (
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" checked={tapPlaceActive ?? false} onChange={onToggleTapPlace} className="rounded-sm" />
          Klikni na mapu za postavljanje
        </label>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex items-center justify-between gap-2 pt-1">
        {onDelete ? (
          <button onClick={onDelete} className="text-xs font-medium text-red-500 hover:text-red-700">Obriši</button>
        ) : <span />}
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-xs font-medium text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-300">
            Zatvori
          </button>
          <button onClick={handleSave} disabled={saving}
            className="text-xs font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 px-3 py-1.5 rounded-lg">
            Primeni
          </button>
        </div>
      </div>
    </div>
  )
}
